import { useMemo } from 'react';
import PropTypes from 'prop-types';

import useDetailTable from '#/hooks/table/useDetailTable';
import DetailTable from '#/modules/commonComponents/table/DetailTable';
import TruncatedTextWithTooltip from '#/modules/commonComponents/table/TruncatedTextWithTooltip';
import MaterialNameStack from '#/modules/imsMgmt/receipts/shared/MaterialNameStack';

function displayDash(value) {
  if (value == null || value === '') {
    return '—';
  }
  return value;
}

/** 簽收明細欄位：DetailTable（欄寬可拖曳）；產品沿用 MaterialNameStack（上名稱下代號）。 */
function buildSignReceiptLineColumns() {
  return [
    {
      id: 'serialNo',
      header: '序號',
      accessorFn: (row) => displayDash(row.serialNo),
      size: 150,
      enableSorting: false,
      meta: { cellClassName: 'td_left font-medium' },
      cell: ({ getValue }) => <TruncatedTextWithTooltip text={getValue()} />,
    },
    {
      id: 'product',
      header: '產品',
      size: 240,
      enableSorting: false,
      meta: { cellClassName: 'td_left' },
      cell: ({ row }) => (
        <MaterialNameStack
          name={row.original.itemName}
          code={row.original.productCode}
        />
      ),
    },
    {
      id: 'problemDescription',
      header: '問題描述',
      accessorFn: (row) => displayDash(row.problemDescription),
      size: 220,
      enableSorting: false,
      meta: { cellClassName: 'td_left' },
      cell: ({ getValue }) => <TruncatedTextWithTooltip text={getValue()} />,
    },
    {
      id: 'accessories',
      header: '配件',
      accessorFn: (row) => displayDash(row.accessories),
      size: 160,
      enableSorting: false,
      meta: { cellClassName: 'td_left' },
      cell: ({ getValue }) => (
        <TruncatedTextWithTooltip
          text={getValue()}
          contentClassName="whitespace-pre-line"
        />
      ),
    },
    {
      id: 'report',
      header: '報告',
      accessorFn: (row) => {
        if (!row.repairReportNo) {
          return '—';
        }
        return row.repairReportStatus
          ? `${row.repairReportNo}（${row.repairReportStatus}）`
          : row.repairReportNo;
      },
      size: 190,
      enableSorting: false,
      meta: { cellClassName: 'td_left' },
      cell: ({ getValue }) => <TruncatedTextWithTooltip text={getValue()} />,
    },
    {
      id: 'resolution',
      header: '處理結果',
      accessorFn: (row) => displayDash(row.resolution ?? row.refuseReason),
      size: 240,
      enableSorting: false,
      meta: { cellClassName: 'td_left' },
      cell: ({ getValue }) => <TruncatedTextWithTooltip text={getValue()} />,
    },
  ];
}

const SignReceiptLinesTable = ({ lines = [] }) => {
  const rows = useMemo(
    () => (lines ?? []).map((line, index) => ({
      ...line,
      id: String(line.id ?? line.repairItemId ?? `line-${index}`),
    })),
    [lines],
  );
  const columns = useMemo(() => buildSignReceiptLineColumns(), []);
  const detailTableState = useDetailTable(rows, columns, {
    enableExpanding: false,
    getRowId: (row) => String(row.id),
  });

  if (!rows.length) {
    return <p className="px-4 py-6 text-sm text-slate-500">尚無簽收品項</p>;
  }

  return (
    <div className="detail-table-container">
      <DetailTable
        state={detailTableState}
        containerClassName="detail-table-scroll--section-embedded"
        tableClassName="detail-table--flat-lines"
      />
    </div>
  );
};

SignReceiptLinesTable.propTypes = {
  lines: PropTypes.arrayOf(PropTypes.object),
};

export default SignReceiptLinesTable;
